import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import {
	Railroad as RailroadEntity,
	JobSummary,
	FieldWorker,
	Customer,
} from 'src/shared/api';
import * as RailroadDetailActions from './railroad-detail.actions';
import * as RailroadLogoActions from '../general-info/railroad-logo/railroad-logo.actions';

export const railroadDetailFeatureKey = 'railroad-detail';

interface Railroad extends RailroadEntity {
	logo?: string;
	jobs?: JobSummary[];
}

export interface RailroadDetailsState extends EntityState<Railroad> {
	selectedRailroadId?: number;
	error?: any;
	isLoading?: boolean;
	isLoadingLogo?: boolean;
	shouldStayOnPage?: boolean;
	customers?: Customer[];
	jobs?: JobSummary[];
	rwics?: FieldWorker[];
	isSaving?: boolean;
}

export const railroadDetailAdapter: EntityAdapter<Railroad> = createEntityAdapter<
	Railroad
>();

export interface RailroadDetailPartialState {
	readonly [railroadDetailFeatureKey]: RailroadDetailsState;
}

export const initialState: RailroadDetailsState = railroadDetailAdapter.getInitialState(
	{
		isLoading: true,
		isLoadingLogo: true,
		isSaving: false,
		railroads: [],
		jobs: [],
		rwics: [],
		// additional entity state properties
	}
);

const railroadsReducer = createReducer(
	initialState,
	on(RailroadDetailActions.railroadLoadedFromStore, (state) => ({
		...state,
		isLoading: false,
	})),
	on(
		RailroadLogoActions.logoSuccessfullyUploadedToApi,
		RailroadLogoActions.logoLoadedFromStore,
		(state) => ({ ...state, isLoadingLogo: false })
	),
	on(RailroadDetailActions.saveRequested, (state, { shouldStayOnPage }) => ({
		...state,
		isSaving: true,
		shouldStayOnPage,
	})),
	on(RailroadDetailActions.saveRequestedWithInvalidForm, (state) => ({
		...state,
		error: 'Form Invalid',
	})),
	on(RailroadDetailActions.formErrorCleared, (state) => ({
		...state,
		error: undefined,
	})),
	on(RailroadDetailActions.newRailroadSaved, (state) =>
		railroadDetailAdapter.removeOne(0, state)
	),
	on(RailroadDetailActions.newRailroadSaved, (state, { railroad }) =>
		railroadDetailAdapter.addOne(railroad, { ...state, isSaving: false })
	),
	on(RailroadDetailActions.railroadSaved, (state, { railroad }) =>
		railroadDetailAdapter.updateOne(
			{
				id: railroad.id,
				changes: {
					...railroad,
				},
			},
			{ ...state, isSaving: false }
		)
	),
	on(RailroadDetailActions.railroadSelected, (state, { railroadId }) => ({
		...state,
		selectedRailroadId: railroadId,
	})),
	on(RailroadDetailActions.railroadLoadedFromApi, (state, { railroad }) =>
		railroadDetailAdapter.upsertOne(railroad, {
			...state,
			isLoading: false,
		})
	),
	on(
		RailroadLogoActions.logoLoadedFromApi,
		RailroadLogoActions.logoSuccessfullyBuffered,
		(state, { logo }) =>
			railroadDetailAdapter.upsertOne(
				{ id: state.selectedRailroadId, logo },
				{ ...state, isLoadingLogo: false }
			)
	),
	on(
		RailroadLogoActions.logoUploaded,
		RailroadLogoActions.userClickedRemoveLogo,
		(state) => ({
			...state,
			isLoadingLogo: true,
		})
	),
	on(RailroadLogoActions.logoRemoved, (state) =>
		railroadDetailAdapter.updateOne(
			{ id: state.selectedRailroadId, changes: { logo: null } },
			{ ...state, isLoadingLogo: false }
		)
	),
	on(RailroadDetailActions.customerssLoaded, (state, { customers }) => ({
		...state,
		customers,
	})),
	on(RailroadDetailActions.jobsLoaded, (state, { jobs }) => ({
		...state,
		jobs,
	})),
	on(RailroadDetailActions.rwicsLoaded, (state, { rwics }) => ({
		...state,
		rwics,
	})),
	on(RailroadDetailActions.errorSavingRailroad, (state, { error }) => ({
		...state,
		error: error,
		isSaving: false,
	})),
	on(RailroadDetailActions.newRailroadStarted, (state) =>
		railroadDetailAdapter.addOne({ id: 0 }, { ...state, isLoading: false })
	),
	on(RailroadDetailActions.userReportableErrorSavingRailroad, (state) => ({
		...state,
		isSaving: false,
	})),
	on(RailroadDetailActions.railroadDetailDestroyed, (state) => ({
		...state,
		isLoading: true,
		isLoadingLogo: true,
		error: undefined,
		railroads: [],
		jobs: [],
		rwics: [],
	}))
);

export function reducer(
	state: RailroadDetailsState | undefined,
	action: Action
) {
	return railroadsReducer(state, action);
}
